import { queryKeys } from '@/lib/query-keys';
import { consumptionAlertClient } from '@/lib/api/ts-rest';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDefaultSite } from './useDefaultSite';

interface UseConsumptionAlertsParams {
  organizationId: string | undefined;
  page: number;
  limit: number;
  q?: string;
  /** Inclusive occurredAt window (ISO date strings). */
  from?: string;
  to?: string;
}

export function useConsumptionAlerts({
  organizationId,
  page,
  limit,
  q,
  from,
  to,
}: UseConsumptionAlertsParams) {
  const searchText = q?.trim() || undefined;
  // Scoped to the selected billing centre, like the lines listing. No site
  // selected means the whole organization.
  const { currentSite } = useDefaultSite();

  return useQuery({
    queryKey: queryKeys.alerts.list(
      organizationId,
      currentSite?.id,
      page,
      limit,
      searchText,
      from,
      to,
    ),
    queryFn: async () => {
      const query = { page, limit, q: searchText, from, to };
      const res = currentSite
        ? await consumptionAlertClient.listSiteAlerts({
            params: { organizationId: organizationId!, siteId: currentSite.id },
            query,
          })
        : await consumptionAlertClient.listAlerts({
            params: { organizationId: organizationId! },
            query,
          });
      if (res.status !== 200) throw new Error('Failed to load alerts');
      return res.body;
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  });
}

export interface UseExportConsumptionAlertsParams {
  organizationId: string | undefined;
  q?: string;
  /** Inclusive occurredAt window (ISO date strings). */
  from?: string;
  to?: string;
}

interface UseConsumptionAlertSummaryParams {
  organizationId: string | undefined;
  /** Inclusive occurredAt window (ISO date strings). */
  from?: string;
  to?: string;
}

export function useConsumptionAlertSummary({
  organizationId,
  from,
  to,
}: UseConsumptionAlertSummaryParams) {
  const { currentSite } = useDefaultSite();

  return useQuery({
    queryKey: queryKeys.alerts.summary(
      organizationId,
      currentSite?.id,
      from,
      to,
    ),
    queryFn: async () => {
      const query = { from, to };
      const res = currentSite
        ? await consumptionAlertClient.getSiteAlertSummary({
            params: { organizationId: organizationId!, siteId: currentSite.id },
            query,
          })
        : await consumptionAlertClient.getAlertSummary({
            params: { organizationId: organizationId! },
            query,
          });
      if (res.status !== 200) throw new Error('Failed to load summary');
      return res.body;
    },
    enabled: !!organizationId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
}
